/**
 * Routes. BrowserRouter, not HashRouter: Telegram puts its launch parameters
 * in the hash and the two would fight over it.
 */
import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from '@/auth/AuthProvider'
import { ListSkeleton, Screen } from '@/components'

const Home = lazy(() => import('@/screens/Home'))
const Login = lazy(() => import('@/screens/Login'))
const TelegramCallback = lazy(() => import('@/screens/TelegramCallback'))
const Nearby = lazy(() => import('@/screens/Nearby'))
const NameSearch = lazy(() => import('@/screens/NameSearch'))
const PhotoSearch = lazy(() => import('@/screens/PhotoSearch'))
const Dialog = lazy(() => import('@/screens/Dialog'))
const History = lazy(() => import('@/screens/History'))
const Profile = lazy(() => import('@/screens/Profile'))
const Tariffs = lazy(() => import('@/screens/Tariffs'))
const PaymentResult = lazy(() => import('@/screens/PaymentResult'))
const Legal = lazy(() => import('@/screens/Legal'))
const Coverage = lazy(() => import('@/screens/Coverage'))

function Loading() {
  return (
    <Screen>
      <ListSkeleton rows={3} />
    </Screen>
  )
}

export default function AppRoutes() {
  const { ready, isAuthenticated } = useAuth()

  if (!ready) return <Loading />

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/telegram" element={<TelegramCallback />} />
          <Route path="/legal/:document" element={<Legal />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    )
  }

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/nearby" element={<Nearby />} />
        <Route path="/search" element={<NameSearch />} />
        <Route path="/photo" element={<PhotoSearch />} />
        {/* `new` is a valid id here: it starts a conversation from a results
            screen. A separate static route would win over this one and leave
            the screen without an :id param at all. */}
        <Route path="/dialog/:id" element={<Dialog />} />
        <Route path="/history" element={<History />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/coverage" element={<Coverage />} />
        <Route path="/tariffs" element={<Tariffs />} />
        <Route path="/payment/:orderId" element={<PaymentResult />} />
        <Route path="/legal/:document" element={<Legal />} />
        <Route path="/auth/telegram" element={<TelegramCallback />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
